import { useEffect, useMemo, useState } from 'react';
import { Wrapper, Header } from '../../../forms/Form';
import { API_ROOT, sendRequest } from '../../../../supplementary_components/requests';
import { LoadingIcon } from '../../../../supplementary_components/loading_icon';
import { priceOnFinish } from '../../../../supplementary_components/priceInputDelegate';
import { popupComponents$ } from '../../../../supplementary_components/store';
import {
    readOnlyTruckInvoiceColumns,
    renderInvoiceColumnCell,
} from '../../../../pages/reports/payableInvoicesManagement';
import listStyles from '../../list_view/listView.module.scss';
import reportStyles from '../../../../pages/reports/reports.module.scss';

const POPUP_KIND = 'truckMaintenanceInvoices';
const GRID_COLUMNS = '0.55fr 0.8fr 0.9fr 0.75fr 0.75fr 1.3fr 0.8fr 1.6fr 12rem 8rem';

export function sumInvoiceAmounts(rows) {
    return (rows || []).reduce((sum, row) => sum + (Number(row?.amount) || 0), 0);
}

export function openTruckMaintenanceInvoicesPopup({ truckId, truckNumber }) {
    const withoutExisting = popupComponents$.getValue().filter(
        (popup) => popup.popupData?.popupKind !== POPUP_KIND,
    );
    popupComponents$.next([
        ...withoutExisting,
        {
            content: TruckMaintenanceInvoicesPopup,
            popupData: { popupKind: POPUP_KIND, truckId, truckNumber },
            EZCloseAllowed: true,
            customWidth: 'min(96rem, 94vw)',
        },
    ]);
}

const TruckMaintenanceInvoicesPopup = ({ popupData }) => {
    const truckId = popupData?.truckId;
    const truckNumber = popupData?.truckNumber;
    const columns = useMemo(() => readOnlyTruckInvoiceColumns(), []);
    const [status, setStatus] = useState('loading');
    const [rows, setRows] = useState([]);

    useEffect(() => {
        if (truckId == null) return undefined;
        let cancelled = false;
        sendRequest(
            'GET',
            `${API_ROOT}invoices/by-truck/${truckId}/all`,
            null,
            (data) => {
                if (cancelled) return;
                if (!Array.isArray(data)) {
                    setStatus('error');
                    return;
                }
                setRows(data);
                setStatus('ready');
            },
            () => {
                if (!cancelled) setStatus('error');
            },
        );
        return () => {
            cancelled = true;
        };
    }, [truckId]);

    const title = truckNumber
        ? `All Maintenance Invoices - Truck ${truckNumber}`
        : 'All Maintenance Invoices';
    const totalLabel = status === 'ready' ? `Total: ${priceOnFinish(sumInvoiceAmounts(rows))}` : null;

    return (
        <Wrapper onClick={(e) => e.stopPropagation()} style={{ display: 'flex', flexDirection: 'column', maxHeight: '85vh' }}>
            <Header style={{ flexShrink: 0, position: 'relative', zIndex: 2 }}>
                <span>{title}</span>
                {totalLabel ? <span style={{ marginLeft: 'auto' }}>{totalLabel}</span> : null}
            </Header>
            <div style={{ overflowY: 'auto', minHeight: 0, padding: '1.5rem 2rem 2rem' }}>
                {status === 'loading' ? <LoadingIcon state height="10rem" wrapperHeight="10rem" /> : null}
                {status === 'error' ? <div className="errorMessage">Something went wrong</div> : null}
                {status === 'ready' ? (
                    <div className={listStyles.container} style={{ '--list-columns': GRID_COLUMNS }}>
                            <div
                                className={listStyles.listViewHeaderWrapper}
                                style={{ gridTemplateColumns: 'var(--list-columns)', position: 'static' }}
                            >
                                {columns.map((col) => (
                                    <div key={col.columnName} className={listStyles.listHeaderLabel}>
                                        <span className={listStyles.labelText}>{col.label}</span>
                                    </div>
                                ))}
                            </div>
                            {rows.length === 0 ? (
                                <div style={{ display: 'grid', gridTemplateColumns: 'var(--list-columns)', width: '100%' }}>
                                    <div className={reportStyles.expensesEmptyBanner} style={{ gridColumn: '1 / -1' }}>
                                        No maintenance invoices.
                                    </div>
                                </div>
                            ) : rows.map((invoice) => (
                                <div key={invoice.id ?? invoice.invoiceNumber} className={listStyles.row}>
                                    {columns.map((col) => (
                                        <div
                                            key={col.columnName}
                                            style={col.columnName === 'description' || col.columnName === 'shopName'
                                                ? { minWidth: 0, overflow: 'hidden' }
                                                : undefined}
                                        >
                                            {renderInvoiceColumnCell(col, invoice)}
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                ) : null}
            </div>
        </Wrapper>
    );
};
